import {
  createContext,
  createEffect,
  createMemo,
  useContext,
  type Accessor,
  type Component,
  type ParentProps
} from 'solid-js'
import { useUser } from '../providers/user-provider'
import { FEATURE_FLAG_KEYS, FLAG_DEFAULTS, type FeatureFlagKey } from './registry'
import { resolve } from './resolver'
import { createOverridesSignal } from './overrides'
import { posthogIdentify, posthogSetArtistGroup, usePosthogFlagsSignal } from './posthog-bridge'

type FeatureFlagsContextValue = {
  readonly isEnabled: Accessor<(key: FeatureFlagKey) => boolean>
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined)

export const FeatureFlagsProvider: Component<ParentProps> = (props) => {
  const { currentUser, organizationSlug } = useUser()
  const [overrides] = createOverridesSignal()
  const posthogFlags = usePosthogFlagsSignal()

  // Identify the user to PostHog whenever they change.
  createEffect(() => {
    const user = currentUser()
    if (user !== undefined) {
      posthogIdentify({ id: user.id, email: user.email })
    }
  })

  // Register the artist group with PostHog whenever the selected organization
  // changes, so group-targeted flags evaluate correctly for the current org.
  createEffect(() => {
    const slug = organizationSlug()
    if (slug !== undefined) {
      posthogSetArtistGroup(slug)
    }
  })

  const isEnabled = createMemo(() => {
    // Snapshot all reactive inputs here so the returned closure stays stable
    // within a single render but updates when any input changes.
    const overridesMap = overrides()
    const flagsMap = posthogFlags()
    return (key: FeatureFlagKey): boolean =>
      resolve({
        key,
        overrides: overridesMap,
        posthogFlags: flagsMap,
        defaults: FLAG_DEFAULTS
      })
  })

  return <FeatureFlagsContext.Provider value={{ isEnabled }}>{props.children}</FeatureFlagsContext.Provider>
}

/**
 * Reactive boolean accessor for a single feature flag.
 * Type-safe: passing an unknown key is a compile error.
 *
 * @example
 *   const isNewDashboard = useFeature('new-dashboard')
 *   return <Show when={isNewDashboard()} fallback={<OldDashboard />}><NewDashboard /></Show>
 */
export function useFeature(key: FeatureFlagKey): Accessor<boolean> {
  const ctx = useContext(FeatureFlagsContext)
  if (ctx === undefined) {
    throw new Error(
      'useFeature must be called inside a <FeatureFlagsProvider>. ' +
        'Mount the provider in App.tsx above the component tree that calls useFeature.'
    )
  }
  return () => ctx.isEnabled()(key)
}

/**
 * All flags at once, for debug panels and developer tooling.
 */
export function useFeatures(): Accessor<Record<FeatureFlagKey, boolean>> {
  const ctx = useContext(FeatureFlagsContext)
  if (ctx === undefined) {
    throw new Error(
      'useFeatures must be called inside a <FeatureFlagsProvider>. ' +
        'Mount the provider in App.tsx above the component tree that calls useFeatures.'
    )
  }
  return createMemo(
    () => Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, ctx.isEnabled()(k)])) as Record<FeatureFlagKey, boolean>
  )
}
